/**
 * Posts the snapshot payload to the specified API endpoint to create a shareable link.
 *
 * @param url - API endpoint URL.
 * @param payload - Snapshot payload data.
 * @param timeoutMs - Request timeout in milliseconds (default: 10000).
 * @returns The created snapshot's id, or an empty string if creation failed.
 */
export async function postSnapshotLink(
	url: string,
	payload: any,
	timeoutMs = 10000,
): Promise<string> {
	if (!url) {
		console.error('[ShedBuilder] Snapshot URL is not configured');
		return '';
	}

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeoutMs);

	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ action: 'create', snapshotData: payload }),
			signal: controller.signal,
		});

		if (!response.ok) {
			console.error(`[ShedBuilder] Snapshot API response HTTP ${response.status}`);
			return '';
		}

		const result = await response.json();
		return result?.success && result?.id ? result.id : '';
	} catch (error) {
		if ((error as Error).name === 'AbortError') {
			console.error('[ShedBuilder] Snapshot request timeout');
		} else {
			console.error('[ShedBuilder] Error:', error);
		}
		return '';
	} finally {
		clearTimeout(timer);
	}
}
